// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the store-reading side of "Open Konvo on". StartPageModel holds the
// rules; this feeds them the live form factor, rail tabs and sidebar pages.

import {Routes} from '@app/app/Routes';
import {visibleMobileRailTabs} from '@app/features/app/components/layout/mobile_rail/MobileRailTypes';
import {visibleSidebarPages} from '@app/features/app/components/layout/sidebar_nav/SidebarPages';
import {
	currentDesktopSidebar,
	currentSidebarPageConditions,
	isAgentsEnabled,
} from '@app/features/app/components/layout/sidebar_nav/SidebarPagesLive';
import KonvoCalendarPrefs from '@app/features/konvo_calendar/state/KonvoCalendarPrefs';
import StartPage from '@app/features/navigation/state/StartPage';
import {
	isRememberableStart,
	phoneStartOptions,
	resolveStartTarget,
	type StartDestination,
	type StartFormFactor,
	type StartTarget,
	startDestinationForPath,
} from '@app/features/navigation/utils/StartPageModel';
import MobileLayout from '@app/features/ui/state/MobileLayout';

export function currentStartFormFactor(): StartFormFactor {
	return MobileLayout.enabled ? 'phone' : 'desktop';
}

/**
 * The destinations offered for a form factor. At launch WhatsApp's link state and
 * agents access are not known yet, so a choice of either holds (each page handles
 * an unlinked account or missing access); Settings only offers them once they are
 * there, like the sidebar. Desktop pages the user switched off are not offered.
 */
export function startOptions(formFactor: StartFormFactor, atLaunch = false): Array<StartDestination> {
	if (formFactor === 'phone') {
		const tabs = atLaunch
			? KonvoCalendarPrefs.mobileRailTabs
			: visibleMobileRailTabs(KonvoCalendarPrefs.mobileRailTabs, isAgentsEnabled());
		return phoneStartOptions(tabs);
	}
	return visibleSidebarPages(currentSidebarPageConditions({assumeConnected: atLaunch}), currentDesktopSidebar());
}

/** Where a launch at `pathname` should land, or null when the URL is a deep link that must win. */
export function getLaunchStartTarget(pathname: string = window.location.pathname): StartTarget | null {
	const formFactor = currentStartFormFactor();
	return resolveStartTarget({
		formFactor,
		choice: StartPage.choiceFor(formFactor),
		remembered: StartPage.lastFor(formFactor),
		available: startOptions(formFactor, true),
		isDeepLink: pathname !== Routes.HOME,
	});
}

/** The destination the current route counts as for "Last used", if it should be remembered. */
export function rememberableStartFor(pathname: string, chatsFilter: string): StartDestination | null {
	const formFactor = currentStartFormFactor();
	const destination = startDestinationForPath(pathname, {
		formFactor,
		phoneTabs: KonvoCalendarPrefs.mobileRailTabs,
		chatsFilter,
	});
	if (!destination) return null;
	return isRememberableStart(destination, formFactor, startOptions(formFactor)) ? destination : null;
}
